import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Fingerprint, Clock, Palmtree, Stethoscope, BarChart3, HelpCircle, CalendarDays, KeyRound, FileDown, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

// Guida passo-passo per i dipendenti: timbrature, ferie, permessi, malattia e certificato medico
const Img = ({ src, alt }) => (
  <div className="flex justify-center my-4">
    <img src={src} alt={alt} loading="lazy"
      className="w-full max-w-[290px] rounded-2xl border border-slate-200 shadow-md" />
  </div>
);

const Step = ({ n, children }) => (
  <li className="flex gap-3 items-start">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-900 text-white text-xs font-bold flex items-center justify-center mt-0.5">{n}</span>
    <span className="text-slate-700 text-sm leading-relaxed">{children}</span>
  </li>
);

const Tip = ({ children }) => (
  <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-xl px-4 py-3 my-3">
    <p className="text-emerald-900 text-sm leading-relaxed">💡 {children}</p>
  </div>
);

const Warn = ({ children }) => (
  <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl px-4 py-3 my-3">
    <p className="text-amber-900 text-sm leading-relaxed">⚠️ {children}</p>
  </div>
);

const Section = ({ id, icon: Icon, num, title, children }) => (
  <section id={id} className="scroll-mt-20 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-7">
    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2.5 mb-4">
      <span className="w-8 h-8 rounded-lg bg-blue-900 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">{num}</span>
      <Icon className="h-5 w-5 text-blue-800 flex-shrink-0" /> {title}
    </h2>
    {children}
  </section>
);

const TOC = [
  ['accesso', '1. Primo accesso all\'app'],
  ['passkey', '2. Registra la passkey (una sola volta)'],
  ['timbrare', '3. Timbrare ogni giorno'],
  ['saldi', '4. I tuoi saldi ferie e permessi'],
  ['ferie', '5. Richiedere le ferie'],
  ['permesso', '6. Richiedere un permesso orario'],
  ['malattia', '7. Malattia e certificato medico'],
  ['riepilogo', '8. Il riepilogo annuale'],
  ['faq', '9. Problemi frequenti (FAQ)'],
];

export default function GuidaDipendente() {
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await axios.get(`${API}/hr/guide/pdf`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'guida_dipendenti_hwsmart.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Errore nel generare il PDF, riprova.'); }
    finally { setPdfBusy(false); }
  };
  return (
    <div className="min-h-screen bg-slate-100" data-testid="guida-dipendente-page">
      <div className="bg-blue-900 text-white">
        <div className="max-w-2xl mx-auto px-5 py-6">
          <div className="flex items-center justify-between mb-3">
            <Link to="/timbratura" data-testid="guida-back-btn"
              className="inline-flex items-center gap-1.5 text-blue-200 hover:text-white text-sm">
              <ArrowLeft className="h-4 w-4" /> Torna alla Timbratura
            </Link>
            <button data-testid="guida-pdf-btn" onClick={downloadPdf} disabled={pdfBusy}
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-blue-400 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
              {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Scarica PDF
            </button>
          </div>
          <h1 className="text-2xl font-bold">📖 Guida per i dipendenti</h1>
          <p className="text-blue-200 text-sm mt-1">Timbrature, ferie, permessi, malattia e certificato medico — passo passo</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 pb-16">

        {/* Indice */}
        <nav className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="guida-indice">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Indice</p>
          <ul className="space-y-1.5">
            {TOC.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="text-blue-800 hover:underline text-sm">{label}</a>
              </li>
            ))}
          </ul>
        </nav>

        {/* 1. PRIMO ACCESSO */}
        <Section id="accesso" icon={KeyRound} num="1" title="Primo accesso all'app">
          <ol className="space-y-3">
            <Step n="1">Apri il browser del tuo telefono (Chrome su Android, Safari su iPhone) e vai su <b>hwsmart.it</b>.</Step>
            <Step n="2">Inserisci l'<b>email</b> e la <b>password temporanea</b> che ti ha comunicato l'amministrazione, poi tocca <b>Accedi</b>.</Step>
            <Step n="3">Al primo accesso ti verrà chiesto di <b>scegliere una nuova password personale</b>: conservala con cura, non condividerla con nessuno.</Step>
            <Step n="4">Dopo l'accesso arrivi direttamente sulla tua pagina <b>Timbratura</b>.</Step>
          </ol>
          <Img src="/guide/01-login.jpg" alt="Schermata di accesso" />
          <Tip>Aggiungi la pagina alla schermata Home del telefono: dal menu del browser scegli <b>"Aggiungi a schermata Home"</b> — così l'app si apre con un tocco, come una vera app.</Tip>
        </Section>

        {/* 2. PASSKEY */}
        <Section id="passkey" icon={Fingerprint} num="2" title="Registra la passkey (una sola volta)">
          <p className="text-slate-700 text-sm leading-relaxed mb-3">
            La <b>passkey</b> è la tua "firma digitale": collega le timbrature alla tua impronta digitale o al tuo volto.
            Serve a garantire che <b>solo tu</b> possa timbrare per te. Si registra <b>una sola volta per telefono</b>.
          </p>
          <ol className="space-y-3">
            <Step n="1">Nella pagina Timbratura tocca il pulsante verde <b>"Registra questo telefono"</b>.</Step>
            <Step n="2">Il telefono ti chiede di confermare con <b>impronta digitale</b>, <b>riconoscimento del volto</b> o il <b>PIN di sblocco</b>: conferma.</Step>
            <Step n="3">Fatto! Vedrai il messaggio "Passkey registrata" e al posto del pulsante comparirà il <b>bottone grande per timbrare</b>.</Step>
          </ol>
          <Img src="/guide/02-passkey.jpg" alt="Registrazione passkey" />
          <Warn>Hai <b>cambiato telefono</b>? Avvisa l'amministrazione: azzererà la vecchia passkey e potrai registrare quella del nuovo telefono con lo stesso pulsante.</Warn>
          <Tip>Su iPhone serve iOS 16 o superiore. Se il pulsante dice che il browser non supporta le passkey, aggiorna Chrome/Safari.</Tip>
        </Section>

        {/* 3. TIMBRARE */}
        <Section id="timbrare" icon={Clock} num="3" title="Timbrare ogni giorno">
          <p className="text-slate-700 text-sm leading-relaxed mb-3">
            Al centro della pagina c'è <b>un solo grande pulsante</b> che ti dice sempre qual è la prossima azione:
            non puoi sbagliare. In una giornata tipo (9:00-13:00 / 14:00-18:00) le timbrature sono 4:
          </p>
          <ol className="space-y-3">
            <Step n="1"><b>🟢 Entra</b> — quando arrivi al lavoro (dalle 8:00 in poi). Tocca il pulsante e conferma con impronta/volto.</Step>
            <Step n="2"><b>☕ Vai in pausa</b> — quando esci per la pausa pranzo (verso le 13:00).</Step>
            <Step n="3"><b>🟢 Rientra dalla pausa</b> — quando torni (verso le 14:00).</Step>
            <Step n="4"><b>🔴 Stop</b> — quando finisci la giornata (verso le 18:00).</Step>
          </ol>
          <Img src="/guide/03-punch.jpg" alt="Pulsante di timbratura Entra" />
          <p className="text-slate-700 text-sm leading-relaxed">Sotto il pulsante, il riquadro <b>"Oggi"</b> mostra tutte le timbrature del giorno con gli orari registrati.</p>
          <Tip>C'è una <b>tolleranza di 10 minuti</b>: se timbri alle 9:07 conta come 9:00, senza segnalazioni. Oltre i 10 minuti viene annotato il ritardo.</Tip>
          <Tip>All'ora della pausa e del rientro compare anche un <b>popup promemoria</b> con il pulsante già pronto.</Tip>
          <Warn><b>Hai dimenticato di timbrare l'uscita?</b> Nessun dramma: la sera il sistema chiude automaticamente la giornata all'orario previsto (es. 18:00). Se invece hai dimenticato un'entrata o c'è un errore, avvisa l'amministrazione che può correggere il singolo giorno.</Warn>
          <Warn>Fuori dal tuo orario di lavoro (weekend, festività, giorni di riposo) il pulsante non compare: è normale.</Warn>
        </Section>

        {/* 4. SALDI */}
        <Section id="saldi" icon={CalendarDays} num="4" title="I tuoi saldi ferie e permessi">
          <p className="text-slate-700 text-sm leading-relaxed mb-3">
            Nel riquadro <b>"I tuoi saldi"</b> vedi sempre quanto ti resta:
          </p>
          <ul className="space-y-2 text-sm text-slate-700 list-none">
            <li>🌴 <b>Giorni di ferie</b> — si usano per le ferie (verde)</li>
            <li>🕐 <b>Ore di permesso</b> — si usano per i permessi orari (azzurro)</li>
          </ul>
          <p className="text-slate-700 text-sm leading-relaxed mt-3">
            Ogni <b>1° del mese</b> i saldi crescono automaticamente con la maturazione prevista dal tuo contratto
            (es. +2,16 giorni di ferie e +4,33 ore di permesso al mese). Quando richiedi un'assenza, i saldi
            vengono <b>scalati subito</b>; se la annulli, ti vengono <b>restituiti</b>.
          </p>
        </Section>

        {/* 5. FERIE */}
        <Section id="ferie" icon={Palmtree} num="5" title="Richiedere le ferie">
          <ol className="space-y-3">
            <Step n="1">Nella sezione <b>"Ferie e Permessi"</b> tocca <b>"+ Richiedi"</b>.</Step>
            <Step n="2">Lascia selezionato <b>🌴 Ferie</b>.</Step>
            <Step n="3">Scegli la data <b>Dal</b> e la data <b>Al</b> (per un solo giorno metti la stessa data).</Step>
            <Step n="4">Aggiungi una <b>nota</b> se vuoi (facoltativa) e tocca <b>"Conferma richiesta"</b>.</Step>
          </ol>
          <Img src="/guide/05-richiesta.jpg" alt="Form richiesta ferie" />
          <Tip><b>Sabati, domeniche e festività nazionali NON consumano giorni di ferie</b>: se chiedi da lunedì a domenica, ti vengono scalati solo i giorni in cui avresti lavorato.</Tip>
          <Warn>La richiesta è <b>immediata</b>, senza approvazione, ma per il customer service vale la <b>regola dei 2 presenti</b>: se con la tua assenza resterebbero meno di 2 colleghi presenti, la richiesta viene bloccata e ti viene detto chi è già assente in quei giorni. In casi particolari chiedi all'amministrazione.</Warn>
          <Tip>Per <b>annullare</b> un'assenza futura tocca la <b>✕ rossa</b> accanto alla riga in "Le mie prossime assenze": i saldi tornano subito disponibili. Se l'assenza è già iniziata, può annullarla solo l'amministrazione.</Tip>
        </Section>

        {/* 6. PERMESSO */}
        <Section id="permesso" icon={Clock} num="6" title="Richiedere un permesso orario">
          <p className="text-slate-700 text-sm leading-relaxed mb-3">
            Il permesso serve per assentarti <b>solo alcune ore</b> in un giorno (visita medica, commissione…).
          </p>
          <ol className="space-y-3">
            <Step n="1">Tocca <b>"+ Richiedi"</b> e seleziona <b>🕐 Permesso</b>.</Step>
            <Step n="2">Scegli il <b>giorno</b> e l'orario <b>Dalle / Alle</b> (es. 14:00 → 18:00 = 4 ore).</Step>
            <Step n="3">Tocca <b>"Conferma richiesta"</b>: le ore vengono scalate dal saldo permessi.</Step>
          </ol>
          <Tip>Nel resto della giornata timbri normalmente: il sistema sa che in quelle ore sei in permesso e conta solo le ore effettivamente lavorate.</Tip>
        </Section>

        {/* 7. MALATTIA */}
        <Section id="malattia" icon={Stethoscope} num="7" title="Malattia e certificato medico">
          <p className="text-slate-700 text-sm leading-relaxed mb-3">
            Se stai male: <b>avvisa subito il tuo responsabile</b> come da prassi, poi registra la malattia nell'app
            (non consuma né ferie né permessi).
          </p>
          <ol className="space-y-3">
            <Step n="1">Tocca <b>"+ Richiedi"</b> e seleziona <b>🤒 Malattia</b>.</Step>
            <Step n="2">Indica le date <b>Dal / Al</b> (se non sai ancora quando rientri, metti le date del certificato medico).</Step>
            <Step n="3">Se hai già il certificato, <b>alleghi subito</b> nel riquadro "📎 Certificato medico": tocca <b>Scegli file</b> e carica il PDF <b>oppure una o più foto</b> del certificato (una foto per pagina va benissimo).</Step>
            <Step n="4">Se il medico ti ha dato il <b>numero di protocollo INPS</b>, scrivilo nel campo dedicato (puoi anche indicare SOLO il protocollo, senza file).</Step>
            <Step n="5">Tocca <b>"Conferma richiesta"</b>.</Step>
          </ol>
          <Img src="/guide/06-malattia.jpg" alt="Registrazione malattia con certificato" />
          <p className="text-slate-700 text-sm leading-relaxed mt-2 mb-1 font-semibold">Aggiungere il certificato in un secondo momento:</p>
          <ol className="space-y-3">
            <Step n="1">In "Le mie prossime assenze" trova la riga della malattia e tocca la <b>📎 graffetta</b>.</Step>
            <Step n="2">Si apre il pannello: carica il file/le foto e/o scrivi il protocollo INPS.</Step>
            <Step n="3">Tocca <b>"Carica e invia alla segreteria"</b>.</Step>
          </ol>
          <Img src="/guide/07-certificato.jpg" alt="Pannello certificato medico" />
          <Tip>Al caricamento il certificato viene <b>inviato automaticamente via email alla segreteria paghe e all'amministrazione</b>: non devi mandare nulla a mano. Dal pannello 📎 puoi anche riscaricare i file caricati.</Tip>
          <Warn>Se dopo <b>2 giorni</b> di malattia non risulta né certificato né protocollo INPS, riceverai una <b>email di promemoria</b> automatica (con l'ufficio in copia). Bastano 2 minuti per metterti in regola dal 📎.</Warn>
        </Section>

        {/* 8. RIEPILOGO ANNUALE */}
        <Section id="riepilogo" icon={BarChart3} num="8" title="Il riepilogo annuale">
          <p className="text-slate-700 text-sm leading-relaxed mb-3">
            Vuoi sapere quante ferie hai fatto quest'anno? In fondo alla sezione "Ferie e Permessi" tocca
            <b> "Riepilogo annuale"</b>:
          </p>
          <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
            <li>tre riquadri con i <b>totali dell'anno</b>: giorni di ferie goduti, ore di permesso, giorni di malattia;</li>
            <li>la <b>tabella per mese</b> con il dettaglio;</li>
            <li>l'<b>elenco di tutte le assenze</b> dell'anno;</li>
            <li>con le frecce <b>‹ ›</b> puoi vedere anche gli anni precedenti.</li>
          </ul>
          <Img src="/guide/08-riepilogo.jpg" alt="Riepilogo annuale assenze" />
        </Section>

        {/* 9. FAQ */}
        <Section id="faq" icon={HelpCircle} num="9" title="Problemi frequenti (FAQ)">
          <div className="space-y-4">
            {[
              ['Ho dimenticato di timbrare (entrata o uscita)',
                'L\'uscita dimenticata viene chiusa automaticamente la sera all\'orario previsto. Per un\'entrata mancata o un orario sbagliato, avvisa l\'amministrazione: può inserire o correggere le ore del singolo giorno.'],
              ['Il pulsante per timbrare non compare',
                'Controlla: 1) hai registrato la passkey? 2) è un tuo giorno/orario di lavoro? Fuori orario, nei weekend e nelle festività il pulsante non c\'è. 3) Se oggi sei in ferie o malattia, la pagina te lo indica al posto del pulsante.'],
              ['La passkey non funziona o ho cambiato telefono',
                'Avvisa l\'amministrazione: azzererà la passkey e potrai registrare di nuovo il telefono con "Registra questo telefono". La cronologia delle timbrature non si perde.'],
              ['Voglio annullare ferie già richieste',
                'Tocca la ✕ rossa sulla riga dell\'assenza in "Le mie prossime assenze": i saldi tornano subito. Se l\'assenza è già iniziata, contatta l\'amministrazione.'],
              ['La richiesta ferie viene bloccata (regola dei 2 presenti)',
                'Nel customer service devono restare almeno 2 colleghi presenti in ogni momento. Il messaggio di errore ti dice chi è già assente: prova date diverse o accordati con l\'amministrazione, che può forzare la richiesta nei casi particolari.'],
              ['Ho sbagliato a caricare il certificato medico',
                'Nessun problema: contatta l\'amministrazione, che può eliminare il file sbagliato; poi ricarichi quello giusto dalla 📎 graffetta.'],
              ['Quanto vale la mia giornata in ore?',
                'Dipende dal tuo orario personale impostato dall\'amministrazione (es. 8 ore, part-time 4 ore il giovedì, ecc.). Ferie e riepiloghi tengono già conto del TUO orario.'],
            ].map(([q, a]) => (
              <div key={q} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                <p className="text-sm font-bold text-slate-800">❓ {q}</p>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
            <p className="text-blue-900 text-sm">Per qualsiasi altro dubbio scrivi all'amministrazione:<br />
              <a href="mailto:mas.marchese@holidayw.eu" className="font-bold underline">mas.marchese@holidayw.eu</a></p>
          </div>
        </Section>

        <div className="text-center">
          <Link to="/timbratura" data-testid="guida-bottom-back"
            className="inline-flex items-center gap-2 bg-blue-900 text-white text-sm font-bold px-6 py-3 rounded-xl hover:opacity-90">
            <ArrowLeft className="h-4 w-4" /> Torna alla Timbratura
          </Link>
        </div>
      </div>
    </div>
  );
}
